import { useState } from "react";
import { observer } from "mobx-react-lite";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTran, useUnsavedChanges } from "@/hooks";
import { Button } from "@/components/ui/button";
import { UnsavedChangesDialog } from "@/components/common";
import StepSelectYear from "./steps/StepSelectYear";
import StepSelectFramework from "./steps/StepSelectFramework";
import StepSelectOrganizations from "./steps/StepSelectOrganizations";
import StepReviewConfirm from "./steps/StepReviewConfirm";
import { PageHeader } from "@/components/common";
import { CalendarDays } from "lucide-react";
import {
  createReportingYear,
  getReportingYearById,
  loadReportingYears,
  updateReportingYear,
} from "../operations/reporting-boundary.ops";
import { useEffect } from "react";
import { useOrganizationStore } from "@/features/organization";
import { useAuthStore } from "@/features/auth";
import { useMemo } from "react";
import { reportingYearStore } from "../stores/reporting-year.store";
import { AlertTriangle } from "lucide-react";

const STEPS = [
  { id: 1, label: "Select Year", description: "Year type and period" },
  { id: 2, label: "Select Framework", description: "Frameworks in Scope" },
  {
    id: 3,
    label: "Select Organizations",
    description: "Organizations in scope",
  },
  { id: 4, label: "Review & Confirm", description: "Review your selections" },
];

const ReportingBoundaryNote = ({ t }) => (
  <div className="px-4 pb-4">
    <div className="rounded-radius border border-amber-200 bg-amber-50/80 px-4 py-2 shadow-sm">
      <div className="flex gap-3">
        {/* <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" /> */}
        <div className="min-w-0">
          {/* <p className="text-sm font-bold text-amber-800">
            {t("Important Note")}
          </p> */}
          <div className="flex gap-2 text-xs leading-5 text-foreground dark:text-background">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              {t(
                "You can add new frameworks or organisations anytime in Reporting Boundary edit.",
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AddReportingYearPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  const { availableFrameworks } = reportingYearStore;

  const frameworks = availableFrameworks.filter(
    (framework) => framework.code !== "UNIVERSAL",
  );
  const { user, isJJSEA } = useAuthStore();
  const { reportingYearId } = useParams();
  const isEditForm = !!reportingYearId;

  const [disableYearSelection, setDisableYearSelection] = useState(false);
  const [usedFrameworks, setUsedFrameworks] = useState([]);
  const [usedOrganizations, setUsedOrganizations] = useState([]);
  const [reportingYears, setReportingYears] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const [reportingYear, setReportingYear] = useState(null);

  const [formData, setFormData] = useState({
    startYear: null,
    startMonth: null,
    endYear: null,
    endMonth: null,
    isAllOrganization: false,
    isCompanySelected: false,
    frameworks: [],
    organizations: [],
  });

  const getCurrentCompanyId = (user) =>
    user?.companyId || user?.company?._id || user?.company?.id || user?.orgId;

  const companyId = getCurrentCompanyId(user);

  const getOrganizationIds = (nodes = []) => {
    const ids = [];

    const walk = (items) => {
      items.forEach((org) => {
        ids.push(org.id || org._id || org.key);

        if (org.children?.length) {
          walk(org.children);
        }
      });
    };

    walk(nodes);

    return ids;
  };

  const normalize = (data) => ({
    ...data,
    frameworks: [...data.frameworks].sort(),
    organizations: [...data.organizations].sort(),
  });

  const isChanged = useMemo(() => {
    if (isEditForm) {
      if (!originalData) return false;
      return (
        JSON.stringify(normalize(originalData)) !==
        JSON.stringify(normalize(formData))
      );
    }
    return (
      formData.startYear !== null ||
      formData.startMonth !== null ||
      formData.frameworks.length > 0 ||
      formData.organizations.length > 0
    );
  }, [originalData, formData, isEditForm]);

  const { showDialog, confirmDiscard, cancelDiscard, bypass } =
    useUnsavedChanges(isChanged);

  const {
    fetchHierarchy,
    tree: organizationTree,
    loading: organizationLoading,
  } = useOrganizationStore();

  useEffect(() => {
    if (organizationTree.length > 0 || organizationLoading) return;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy, organizationLoading, organizationTree.length]);

  useEffect(() => {
    const fetchReportingYears = async () => {
      const data = await loadReportingYears({ force: true });
      setReportingYears(data);
    };

    fetchReportingYears();
  }, []);

  useEffect(() => {
    if (!reportingYearId) return;

    const fetchReportingYear = async () => {
      const reportingYear = await getReportingYearById(reportingYearId);
      const allOrganizationIds = getOrganizationIds(organizationTree);

      setReportingYear(reportingYear);
      setDisableYearSelection(reportingYear?.isUsed);

      setUsedFrameworks(reportingYear?.usedFrameworks || []);
      setUsedOrganizations(
        reportingYear?.isUsedAllOrganization
          ? allOrganizationIds
          : (reportingYear?.usedOrganizations ?? []),
      );

      const organizationIds = (reportingYear?.organizations ?? []).map((org) =>
        typeof org === "string" ? org : String(org._id),
      );

      const organizations = reportingYear?.isAllOrganization
        ? allOrganizationIds
        : organizationIds;

      if (reportingYear) {
        const data = {
          startYear: reportingYear.startYear,
          startMonth: reportingYear.startMonth,
          endYear: reportingYear.endYear,
          endMonth: reportingYear.endMonth,
          isAllOrganization: reportingYear.isAllOrganization,
          isCompanySelected: reportingYear.isCompanySelected,
          frameworks: reportingYear.frameworkCodes,
          organizations: organizations,
        };
        setOriginalData(data);
        setFormData(data);
      }
    };

    fetchReportingYear();
  }, [reportingYearId, organizationTree]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getReportingYearId = (item = {}) =>
    item.id || item._id || item.reportingYearId;

  const toMonthIndex = (year, month) => Number(year) * 12 + Number(month);

  const selectedPeriodValidationMessage = (() => {
    if (
      !formData.startYear ||
      !formData.startMonth ||
      !formData.endYear ||
      !formData.endMonth
    ) {
      return "";
    }

    const selectedStart = toMonthIndex(formData.startYear, formData.startMonth);
    const selectedEnd = toMonthIndex(formData.endYear, formData.endMonth);
    const isUnchangedEditPeriod =
      isEditForm &&
      originalData &&
      Number(formData.startYear) === Number(originalData.startYear) &&
      Number(formData.startMonth) === Number(originalData.startMonth) &&
      Number(formData.endYear) === Number(originalData.endYear) &&
      Number(formData.endMonth) === Number(originalData.endMonth);

    if (isUnchangedEditPeriod) {
      return "";
    }

    const hasOverlappingPeriod = reportingYears.some((item) => {
      const isCurrentItem =
        reportingYearId &&
        String(getReportingYearId(item)) === String(reportingYearId);

      if (
        isCurrentItem ||
        !item.startYear ||
        !item.startMonth ||
        !item.endYear ||
        !item.endMonth
      ) {
        return false;
      }

      const existingStart = toMonthIndex(item.startYear, item.startMonth);
      const existingEnd = toMonthIndex(item.endYear, item.endMonth);

      return selectedStart <= existingEnd && selectedEnd >= existingStart;
    });

    return hasOverlappingPeriod
      ? "The selected reporting period overlaps with an existing reporting year. Please select a different period."
      : "";
  })();

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSubmit = () => {
    const payload = {
      ...formData,
      organizations: formData.isAllOrganization ? [] : formData.organizations,
    };

    if (isEditForm && reportingYearId) {
      updateReportingYear(reportingYearId, payload);
    } else {
      createReportingYear(payload);
    }

    setOriginalData(formData);
    bypass();
    navigate(-1);
  };

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1:
        if (
          !formData.startYear ||
          !formData.startMonth ||
          !formData.endYear ||
          !formData.endMonth
        ) {
          return false;
        }

        if (formData.endYear < formData.startYear) return false;

        if (
          formData.endYear === formData.startYear &&
          formData.endMonth <= formData.startMonth
        ) {
          return false;
        }

        return !selectedPeriodValidationMessage;
      case 2:
        return formData.frameworks.length > 0;
      case 3:
        return formData.organizations.length > 0;
      case 4:
        return (
          formData.frameworks.length > 0 && formData.organizations.length > 0
        );
      default:
        return false;
    }
  };

  const isStepAccessible = (stepId) => {
    if (stepId === 1) return true;

    return STEPS.filter((step) => step.id < stepId).every((step) =>
      isStepComplete(step.id),
    );
  };

  const handleStepClick = (stepId) => {
    if (isStepAccessible(stepId)) {
      setCurrentStep(stepId);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
      {/* Breadcrumb */}
      <PageHeader
        title={
          isEditForm
            ? t("Update Reporting Year")
            : t("Reporting Year Setup Process")
        }
        description={t("Follow the steps below to add a new reporting year.")}
        icon={CalendarDays}
      />

      {/* Page Header + Stepper */}
      <div className="px-6 py-5 border-b border-border bg-card/50">
        <div className="max-w-5xl mx-auto">
          {/* <h1 className="text-lg font-bold text-primary">
            {t("Add Reporting Year")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 mb-5">
            {t("Follow the steps below to add a new reporting year.")}
          </p> */}

          {/* Stepper */}
          <div className="flex items-center w-full">
            {STEPS.map((step, index) => {
              const isCompleted = currentStep > step.id;
              const isActive = currentStep === step.id;
              const isAccessible = isStepAccessible(step.id);
              const isLast = index === STEPS.length - 1;

              return (
                <div
                  key={step.id}
                  className="flex items-center flex-1 last:flex-none"
                >
                  {/* Step Item */}
                  <button
                    type="button"
                    onClick={() => handleStepClick(step.id)}
                    disabled={!isAccessible}
                    aria-current={isActive ? "step" : undefined}
                    className={`
                      flex items-center gap-3 rounded-radius text-left transition-opacity
                      ${
                        isAccessible
                          ? "cursor-pointer hover:opacity-80"
                          : "cursor-not-allowed opacity-60"
                      }
                    `}
                  >
                    {/* Circle */}
                    <div
                      className={`
                        relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full 
                        text-sm font-semibold transition-all duration-300
                        ${
                          isCompleted
                            ? "bg-green-500 text-white shadow-sm shadow-green-500/30"
                            : isActive
                              ? "bg-primary text-primary-foreground shadow-md shadow-primary/30 ring-[3px] ring-primary/20"
                              : "bg-muted text-muted-foreground border border-border"
                        }
                      `}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" strokeWidth={3} />
                      ) : (
                        step.id
                      )}
                    </div>

                    {/* Label */}
                    <div className="hidden md:block">
                      <p
                        className={`text-sm font-medium leading-none ${
                          isActive
                            ? "text-primary"
                            : isCompleted
                              ? "text-green-600"
                              : "text-muted-foreground"
                        }`}
                      >
                        {t(step.label)}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t(step.description)}
                      </p>
                    </div>
                  </button>

                  {/* Connector Line */}
                  {!isLast && (
                    <div className="flex-1 mx-4">
                      <div className="relative h-[2px] w-full bg-border rounded-full overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-all duration-500"
                          style={{
                            width: isCompleted ? "100%" : "0%",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-4 overflow-y-auto">
        <div>
          {currentStep === 1 && (
            <StepSelectYear
              formData={formData}
              updateFormData={updateFormData}
              disableYearSelection={disableYearSelection}
              validationMessage={selectedPeriodValidationMessage}
            />
          )}
          {currentStep === 2 && (
            <StepSelectFramework
              formData={formData}
              updateFormData={updateFormData}
              frameworks={frameworks}
              isEditForm={isEditForm}
              usedFrameworks={usedFrameworks}
            />
          )}
          {currentStep === 3 && (
            <StepSelectOrganizations
              formData={formData}
              updateFormData={updateFormData}
              organizationTree={organizationTree}
              companyId={companyId}
              isEditForm={isEditForm}
              usedOrganizations={usedOrganizations}
            />
          )}
          {currentStep === 4 && (
            <StepReviewConfirm
              formData={formData}
              onBack={handleBack}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              organizationTree={organizationTree}
              frameworks={frameworks}
              isEditForm={isEditForm}
              isChanged={isChanged}
              reportingYear={reportingYear}
            />
          )}
        </div>
      </div>

      {currentStep === 2 && <ReportingBoundaryNote t={t} />}

      {/* Footer */}
      {currentStep < 4 ? (
        <div className="px-6 py-4 border-t border-border bg-card flex items-center justify-between">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? handleCancel : handleBack}
            className="gap-2"
          >
            {currentStep === 1 ? (
              t("Cancel")
            ) : (
              <>
                <ArrowLeft className="h-4 w-4" />
                {t("Back")}
              </>
            )}
          </Button>

          {/* Step indicator (mobile) */}
          <span className="text-xs text-muted-foreground md:hidden">
            {t("Step")} {currentStep} / 4
          </span>

          <Button
            onClick={handleNext}
            disabled={!isStepComplete(currentStep)}
            className="gap-2"
          >
            {currentStep === 1
              ? t("Continue to Select Framework")
              : currentStep === 2
                ? t("Continue to Select Organization")
                : t("Review & Confirm")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
});

export default AddReportingYearPage;
